import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Stack, Box, Checkbox,
    FormControlLabel, FormGroup, TextField, IconButton,
    CircularProgress, ButtonGroup,
} from '@mui/material';
import { Icon } from '@iconify/react';
import moment from 'moment';
import {
    openContactRound,
    completeContactRound,
    updateRoundChannels,
    clearRound,
    selectCurrentRound,
    selectRoundLoading,
} from '../../redux/slices/contactRound';
import { callApi } from '../../utils/apiCall';
import {
    buttonGroupStyle,
    separatorStyle,
    separatorLineStyle,
    separatorTextStyle,
} from '../../styles/FollowUp';

export default function CloseChatPopup({
    open,
    onClose,
    onConfirmClose,
    contactDetails,
    practiceId,
    practiceChoices,
}) {
    const dispatch = useDispatch();
    const currentRound = useSelector(selectCurrentRound);
    const loading = useSelector(selectRoundLoading);

    const [channels, setChannels] = useState({ phone: false, whatsapp: true, email: false });
    const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [notes, setNotes] = useState('');
    const [closeReason, setCloseReason] = useState('');
    const [calledConfirmed, setCalledConfirmed] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const leadId = contactDetails?.leadDetails?.id;
    const treatmentId = contactDetails?.leadDetails?.lead_treatments?.[0]?.id;
    const fullName = [
        contactDetails?.leadDetails?.first_name,
        contactDetails?.leadDetails?.last_name,
    ].filter(Boolean).join(' ').trim();
    const leadName = fullName || contactDetails?.name || 'Unknown';

    useEffect(() => {
        if (open && leadId && treatmentId) {
            dispatch(openContactRound(practiceId, leadId, treatmentId, { whatsapp: true }));
        }
        if (!open) {
            dispatch(clearRound());
            setChannels({ phone: false, whatsapp: true, email: false });
            setSelectedOutcome(null);
            setNotes('');
            setCloseReason('');
            setCalledConfirmed(null);
            setIsSaving(false);
        }
    }, [open, leadId, treatmentId, practiceId, dispatch]);

    const handleChannelToggle = (channel) => {
        const updated = { ...channels, [channel]: !channels[channel] };
        setChannels(updated);
        if (currentRound) {
            dispatch(updateRoundChannels(practiceId, leadId, currentRound.id, updated));
        }
    };

    const handleSaveAndClose = async () => {
        if (!selectedOutcome || isSaving) return;
        setIsSaving(true);

        try {
            if (!currentRound) return;

            const result = await dispatch(
                completeContactRound(practiceId, leadId, currentRound.id, {
                    outcome: selectedOutcome,
                    notes: notes || closeReason,
                })
            );

            if (result) {
                const reason = closeReason || selectedOutcome.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
                onConfirmClose(reason);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isLeadLinked = !!leadId && !!treatmentId;

    const outcomeButtonStyle = (isActive) => ({
        backgroundColor: isActive ? 'rgba(0, 171, 85, 0.08)' : 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(0, 171, 85, 0.06)',
            color: '#00AB55',
            border: 'none',
            boxShadow: isActive ? '0 2px 8px rgba(0, 171, 85, 0.12)' : 'none',
        },
        borderRadius: '14px !important',
        color: isActive ? '#00AB55' : '#637381',
        border: 'none',
        fontSize: '14px',
        fontWeight: isActive ? 600 : 500,
        boxShadow: isActive ? '0 1px 4px rgba(0, 171, 85, 0.08)' : 'none',
        padding: '14px 20px',
        justifyContent: 'flex-start',
        textAlign: 'left',
        textTransform: 'none !important',
        transition: 'all 0.2s ease',
        margin: '0 !important',
    });

    // Non-linked-lead fallback
    if (!isLeadLinked) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Close Conversation</Typography>
                    <IconButton onClick={onClose} size="small">
                        <Icon icon="eva:close-fill" width={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No lead linked. Select a reason to close:
                    </Typography>
                    {practiceChoices?.length > 0 && (
                        <TextField
                            select
                            label="Close Reason"
                            value={closeReason}
                            onChange={(e) => setCloseReason(e.target.value)}
                            fullWidth
                            SelectProps={{ native: true }}
                        >
                            <option value="" />
                            {practiceChoices.map((choice) => (
                                <option key={choice} value={choice}>{choice}</option>
                            ))}
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (leadId && practiceId) {
                                await callApi(
                                    `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                                    'post',
                                    { note: 'OTHER', additional_notes: `Whatsapp Chat Closed: ${closeReason}`, lead: leadId }
                                );
                            }
                            onConfirmClose(closeReason || 'Closed');
                        }}
                        disabled={!closeReason}
                    >
                        Close Conversation
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Resolve Conversation — {leadName}</Typography>
                <IconButton onClick={onClose} size="small">
                    <Icon icon="eva:close-fill" width={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Stack direction="row" spacing={3} sx={{ minHeight: 400 }}>
                    {/* LEFT PANEL — Contact Record */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Channels Used</Typography>
                        <FormGroup row>
                            <FormControlLabel
                                control={<Checkbox checked={channels.whatsapp} onChange={() => handleChannelToggle('whatsapp')} />}
                                label="WhatsApp"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={channels.phone} onChange={() => handleChannelToggle('phone')} />}
                                label="Called"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={channels.email} onChange={() => handleChannelToggle('email')} />}
                                label="Emailed"
                            />
                        </FormGroup>

                        <Box sx={{ my: 2, height: '1px', background: 'rgba(145,158,171,0.12)' }} />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>What happened?</Typography>

                        {/* Primary outcomes — ButtonGroup matching FollowUp.jsx */}
                        <ButtonGroup orientation="vertical" sx={buttonGroupStyle}>
                            <Button
                                onClick={() => setSelectedOutcome('UNABLE_TO_CONTACT')}
                                sx={outcomeButtonStyle(selectedOutcome === 'UNABLE_TO_CONTACT')}
                            >
                                <Icon icon="eva:close-circle-fill" width={18} style={{ marginRight: 10, opacity: 0.7 }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>No Answer</Typography>
                                    <Typography variant="caption" sx={{ color: '#919EAB', fontWeight: 400, fontSize: '11px', lineHeight: 1.4 }}>
                                        No answer, voicemail, or wrong number
                                    </Typography>
                                </Box>
                            </Button>
                            <Button
                                onClick={() => setSelectedOutcome('SPOKE_NOT_BOOKED')}
                                sx={outcomeButtonStyle(selectedOutcome === 'SPOKE_NOT_BOOKED')}
                            >
                                <Icon icon="ant-design:calendar-filled" width={18} style={{ marginRight: 10, opacity: 0.7 }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>Spoke - Not Booked</Typography>
                                    <Typography variant="caption" sx={{ color: '#919EAB', fontWeight: 400, fontSize: '11px', lineHeight: 1.4 }}>
                                        Spoke to patient but they didn&apos;t book
                                    </Typography>
                                </Box>
                            </Button>
                        </ButtonGroup>

                        {/* Separator */}
                        <Box sx={separatorStyle}>
                            <Box sx={separatorLineStyle} />
                            <Typography sx={separatorTextStyle}>or close without follow-up</Typography>
                            <Box sx={separatorLineStyle} />
                        </Box>

                        {/* Other — quiet secondary button */}
                        <Button
                            onClick={() => setSelectedOutcome('OTHER')}
                            variant="outlined"
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                py: 1.5,
                                borderRadius: '14px',
                                borderColor: selectedOutcome === 'OTHER' ? '#637381' : '#e0e0e0',
                                color: '#637381',
                                background: selectedOutcome === 'OTHER' ? 'rgba(145,158,171,0.06)' : 'transparent',
                                fontWeight: selectedOutcome === 'OTHER' ? 600 : 500,
                                '&:hover': { borderColor: '#919EAB', background: '#fafafa' },
                            }}
                            fullWidth
                            startIcon={<Icon icon="mdi:information-outline" width={18} style={{ opacity: 0.7 }} />}
                        >
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>Other</Typography>
                                <Typography variant="caption" sx={{ color: '#919EAB', fontWeight: 400, fontSize: '11px', lineHeight: 1.4 }}>
                                    Close conversation without counting as follow-up
                                </Typography>
                            </Box>
                        </Button>

                        {/* === Outcome-specific second screens === */}

                        {/* NO ANSWER */}
                        {selectedOutcome === 'UNABLE_TO_CONTACT' && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 14px', borderRadius: '10px', background: '#f8f9fa', border: '1.5px solid #e8eaed', mb: 2 }}>
                                    <Icon icon="eva:phone-fill" width={20} style={{ color: '#00AB55', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>Did you also call?</Typography>
                                        <Typography variant="caption" sx={{ color: '#637381' }}>
                                            {contactDetails?.leadDetails?.phone_number || contactDetails?.leadDetails?.lead_contact?.phone || 'No phone number'}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant={calledConfirmed === true ? 'contained' : 'text'}
                                        sx={{
                                            background: calledConfirmed === true ? '#00AB55' : 'transparent',
                                            '&:hover': { background: calledConfirmed === true ? '#007B55' : 'rgba(0,171,85,0.08)' },
                                            textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 2,
                                            color: calledConfirmed === true ? '#fff' : '#637381',
                                        }}
                                        onClick={() => setCalledConfirmed(true)}
                                    >
                                        Yes
                                    </Button>
                                    <Button size="small" sx={{ color: '#919EAB', textTransform: 'none', minWidth: 'auto' }} onClick={() => setCalledConfirmed(false)}>No</Button>
                                </Box>
                                <TextField label="Notes" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth placeholder="e.g., Left voicemail, wrong number" sx={{ mb: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Icon icon="mdi:information-outline" width={14} style={{ color: '#919EAB' }} />
                                    <Typography variant="caption" sx={{ color: '#919EAB' }}>
                                        Will move to Follow up {currentRound?.round_number || ''}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* SPOKE NOT BOOKED */}
                        {selectedOutcome === 'SPOKE_NOT_BOOKED' && (
                            <Box sx={{ mt: 2 }}>
                                <TextField label="What did they say?" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth placeholder="e.g., Wants to think about pricing, will call back next week" sx={{ mb: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Icon icon="mdi:information-outline" width={14} style={{ color: '#919EAB' }} />
                                    <Typography variant="caption" sx={{ color: '#919EAB' }}>
                                        Will move to Follow up {currentRound?.round_number || ''}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* OTHER — action cards */}
                        {selectedOutcome === 'OTHER' && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#637381', fontSize: '12px' }}>What should happen to this lead?</Typography>

                                <Typography variant="caption" sx={{ color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, display: 'block', mb: 1 }}>Close &amp; Archive</Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    {[
                                        { label: 'Not Interested', icon: 'mdi:account-cancel-outline' },
                                        { label: 'Too Expensive', icon: 'mdi:currency-gbp' },
                                        { label: 'Gone Elsewhere', icon: 'mdi:map-marker-off-outline' },
                                    ].map((item) => (
                                        <Button
                                            key={item.label}
                                            variant={closeReason === item.label ? 'contained' : 'outlined'}
                                            color={closeReason === item.label ? 'warning' : 'inherit'}
                                            size="small"
                                            onClick={() => setCloseReason(item.label)}
                                            sx={{ flex: 1, textTransform: 'none', fontSize: '12px', fontWeight: 600, borderColor: '#e0e0e0', borderRadius: '8px', py: 1 }}
                                            startIcon={<Icon icon={item.icon} width={16} />}
                                        >
                                            {item.label}
                                        </Button>
                                    ))}
                                </Box>

                                <Typography variant="caption" sx={{ color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, display: 'block', mb: 1 }}>Close &amp; Flag</Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    {[
                                        { label: 'Spam', icon: 'mdi:block-helper' },
                                        { label: 'Duplicate', icon: 'mdi:content-duplicate' },
                                    ].map((item) => (
                                        <Button
                                            key={item.label}
                                            variant={closeReason === item.label ? 'contained' : 'outlined'}
                                            color={closeReason === item.label ? 'error' : 'inherit'}
                                            size="small"
                                            onClick={() => setCloseReason(item.label)}
                                            sx={{ flex: 1, textTransform: 'none', fontSize: '12px', fontWeight: 600, borderColor: '#e0e0e0', borderRadius: '8px', py: 1 }}
                                            startIcon={<Icon icon={item.icon} width={16} />}
                                        >
                                            {item.label}
                                        </Button>
                                    ))}
                                </Box>

                                <Typography variant="caption" sx={{ color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, display: 'block', mb: 1 }}>Just Close</Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    {[
                                        { label: 'General Chat', icon: 'mdi:chat-outline' },
                                        { label: 'Resolved', icon: 'mdi:check-circle-outline' },
                                    ].map((item) => (
                                        <Button
                                            key={item.label}
                                            variant={closeReason === item.label ? 'contained' : 'outlined'}
                                            color={closeReason === item.label ? 'info' : 'inherit'}
                                            size="small"
                                            onClick={() => setCloseReason(item.label)}
                                            sx={{ flex: 1, textTransform: 'none', fontSize: '12px', fontWeight: 600, borderColor: '#e0e0e0', borderRadius: '8px', py: 1 }}
                                            startIcon={<Icon icon={item.icon} width={16} />}
                                        >
                                            {item.label}
                                        </Button>
                                    ))}
                                </Box>

                                <TextField label="Notes" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth placeholder="Additional details..." sx={{ mt: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                    <Icon icon="mdi:information-outline" width={14} style={{ color: '#919EAB' }} />
                                    <Typography variant="caption" sx={{ color: '#919EAB' }}>
                                        This won&apos;t count as a follow-up
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* RIGHT PANEL — Lead Summary */}
                    <Box sx={{
                        width: 280,
                        borderRadius: '12px',
                        background: '#fff',
                        boxShadow: '0 0 1px rgba(145,158,171,0.2), 0 1px 3px rgba(145,158,171,0.08)',
                        overflow: 'hidden',
                        alignSelf: 'flex-start',
                    }}>
                        <Box sx={{ p: '14px 16px', borderBottom: '1px solid rgba(145,158,171,0.12)' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>Lead Summary</Typography>
                        </Box>
                        <Stack spacing={1.5} sx={{ p: '16px' }}>
                            <Box>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Name</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#212B36' }}>{leadName}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Phone</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#212B36' }}>
                                    {contactDetails?.leadDetails?.phone_number || contactDetails?.leadDetails?.lead_contact?.phone || '—'}
                                </Typography>
                            </Box>
                            {(contactDetails?.leadDetails?.lead_contact?.email || contactDetails?.leadDetails?.email) && (
                                <Box>
                                    <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Email</Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#212B36', wordBreak: 'break-all' }}>
                                        {contactDetails?.leadDetails?.lead_contact?.email || contactDetails?.leadDetails?.email}
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Treatment</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#212B36' }}>
                                    {contactDetails?.leadDetails?.lead_treatments?.[0]?.treatment_name || contactDetails?.leadDetails?.lead_treatments?.[0]?.treatment || '—'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Status</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#212B36' }}>
                                    {contactDetails?.leadDetails?.lead_treatments?.[0]?.status?.replace(/_/g, ' ')?.toLowerCase()?.replace(/^\w/, c => c.toUpperCase()) || '—'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Follow-up</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#212B36' }}>
                                    {currentRound ? `Round ${currentRound.round_number}` : '—'}
                                </Typography>
                            </Box>
                            {contactDetails?.leadDetails?.referral && (
                                <Box>
                                    <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Referral</Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#212B36' }}>
                                        {contactDetails.leadDetails.referral}
                                    </Typography>
                                </Box>
                            )}
                            {contactDetails?.leadDetails?.tags?.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.5 }}>Tags</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {contactDetails.leadDetails.tags.map((tag) => (
                                            <Box
                                                key={tag.id || tag.name}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    background: tag.tag_colour ? `${tag.tag_colour}20` : 'rgba(145,158,171,0.12)',
                                                    color: tag.tag_colour || '#637381',
                                                }}
                                            >
                                                {tag.name}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {contactDetails?.leadDetails?.created_at && (
                                <Box>
                                    <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', color: '#919EAB', letterSpacing: '0.5px', fontWeight: 600, mb: 0.25 }}>Created</Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#212B36' }}>
                                        {moment(contactDetails.leadDetails.created_at).fromNow()}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSaveAndClose}
                    disabled={!selectedOutcome || loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    Save &amp; Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

CloseChatPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirmClose: PropTypes.func.isRequired,
    contactDetails: PropTypes.shape({
        name: PropTypes.string,
        leadDetails: PropTypes.object,
    }),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceChoices: PropTypes.array,
};
